import { Routes } from '@angular/router';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { LoginComponent } from './pages/login/login.component';
import { authGuard } from './core/guards/msal-guard';
import { redirectToDashboardGuard } from './core/guards/redirect-todashboard.guard';
import { RoleGuardGuard } from './core/guards/role-guard.guard';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { getPath, PathUnit } from './consts/path.const';
import { AllAppLinks, AppLink, eventObservationsEnabled } from './consts/app-links.config';

// Component mapping for routes
const componentMap: Record<string, any> = {
  [getPath(PathUnit.Dashboard)]: {
    loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
  },
  [getPath(PathUnit.FleetManagement)]: {
    loadComponent: () => import('./pages/fleet-management/fleet-management.component').then(m => m.FleetManagementComponent),
  },
  [getPath(PathUnit.FleetManagementCreate)]: {
    loadComponent: () => import('./pages/fleet-management/create-fleet/create-fleet.component').then(m => m.CreateFleetComponent),
  },
  [getPath(PathUnit.FleetManagementEdit)]: {
    loadComponent: () => import('./pages/fleet-management/edit-fleet/edit-fleet.component').then(m => m.EditFleetComponent),
  },
  [getPath(PathUnit.FleetManagementView)]: {
    loadComponent: () => import('./pages/fleet-management/view-fleet/view-fleet.component').then(m => m.ViewFleetComponent),
  },
  [getPath(PathUnit.VehicleCheckIns)]: {
    loadComponent: () => import('./pages/fleet-management/vehicle-check-ins/vehicle-check-ins.component').then(m => m.VehicleCheckInsComponent),
  },
  [getPath(PathUnit.ComingSoon)]: {
    loadComponent: () => import('./pages/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent),
  },
  [getPath(PathUnit.ReportsManage)]: {
    loadComponent: () => import('./pages/reports-manage/reports-manage.component').then(m => m.ReportsManageComponent),
  },
  [getPath(PathUnit.DeviationReportCreate)]: {
    loadComponent: () => import('./pages/deviation-report/create-deviation-report/create-deviation-report.component').then(m => m.CreateDeviationReportComponent),
  },
  [getPath(PathUnit.DeviationReportDetail)]: {
    loadComponent: () => import('./pages/deviation-report/deviation-detail/deviation-detail.component').then(m => m.DeviationDetailComponent),
  },
  [getPath(PathUnit.DeviationReportEdit)]: {
    loadComponent: () => import('./pages/deviation-report/edit-deviation-report/edit-deviation-report.component').then(m => m.EditDeviationReportComponent),
  },
  [getPath(PathUnit.DeviationVoiceReport)]: {
    loadComponent: () => import('./pages/deviation-report/voice-report-list/voice-report-list.component').then(m => m.VoiceReportListComponent),
  },
  [getPath(PathUnit.DeviationVoiceReportDetail)]: {
    loadComponent: () => import('./pages/deviation-report/voice-report-detail/voice-report-detail.component').then(m => m.VoiceReportDetailComponent),
  },
  [getPath(PathUnit.EventObservations)]: {
    loadComponent: () => import('./pages/event-observations/event-observations.component').then(m => m.EventObservationsComponent),
  },
  [getPath(PathUnit.EventObservationMobile)]: {
    loadComponent: () => import('./pages/event-observations/event-observations.component').then(m => m.EventObservationsComponent),
  },
  [getPath(PathUnit.DeviationReportStudio)]: {
    loadComponent: () => import('./pages/deviation-report/report-studio/report-studio.component').then(m => m.ReportStudioComponent),
  },
  // [getPath(PathUnit.InspectionReport)]: {
  //   loadComponent: () => import('./pages/inspection-report/inspection-reports-page/inspection-reports-page.component').then(m => m.InspectionReportsPageComponent),
  // },
  [getPath(PathUnit.InspectionReportCreate)]: {
    loadComponent: () => import('./pages/inspection-report/create-inspection-report/create-inspection-report.component').then(m => m.CreateInspectionReportComponent),
  },
  [getPath(PathUnit.InspectionReportDetail)]: {
    loadComponent: () => import('./pages/inspection-report/inspection-report-detail/inspection-report-detail.component').then(m => m.InspectionReportDetailComponent),
  },
  [getPath(PathUnit.InspectionReportEdit)]: {
    loadComponent: () => import('./pages/inspection-report/edit-inspection-report/edit-inspection-report.component').then(m => m.EditInspectionReportComponent),
  },
  [getPath(PathUnit.ImageUploadTest)]: {
    loadComponent: () => import('./pages/image-upload-test/image-upload-test.component').then(m => m.ImageUploadTestComponent),
  },
  [getPath(PathUnit.UXDemo)]: {
    loadComponent: () => import('./pages/ux-demo/ux-demo.component').then(m => m.UXDemoComponent),
  },
  [getPath(PathUnit.HistorySoftwareVersions)]: {
    loadComponent: () => import('./pages/ux-demo/history-software-versions/history-software-versions.component').then(m => m.HistorySoftwareVersionsComponent),
  },
};

// Generate routes from AllAppLinks
function generateRoutesFromLinks(links: AppLink[]): Routes {
  const routes: Routes = [];
  function addRoutes(links: AppLink[]) {
    for (const link of links) {
      if (link.route && componentMap[link.route]) {
        const eventObservationRoute =
          link.route === getPath(PathUnit.EventObservations) ||
          link.route === getPath(PathUnit.EventObservationMobile);
        if (eventObservationRoute && !eventObservationsEnabled) {
          continue;
        }
        // If roleAuthGuard is explicitly set to false, skip adding RoleGuardGuard
        if (link.roleAuthGuard === false) {
          routes.push({
            path: link.route,
            pathMatch: 'full',
            loadComponent: componentMap[link.route].loadComponent,
          });
          continue;
        } // else default to true
        routes.push({
          path: link.route,
          pathMatch: 'full',
          loadComponent: componentMap[link.route].loadComponent,
          canActivate: [RoleGuardGuard],
          data: { roles: link.roles }
        });
      }
      if (link.children) {
        addRoutes(link.children);
      }
    }
  }
  addRoutes(links);
  console.log("🚀 ~ generateRoutesFromLinks ~ routes:", routes)
  return routes;
}

export const routes: Routes = [
  {
    path: getPath(PathUnit.Login),
    component: LoginComponent,
    canActivate: [redirectToDashboardGuard]
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard], // Protect all child routes
    children: [
      {
        path: '',
        redirectTo: getPath(PathUnit.Dashboard),
        pathMatch: 'full',
      },
      ...generateRoutesFromLinks(AllAppLinks),
      { path: getPath(PathUnit.Unauthorized), component: UnauthorizedComponent },
      { path: getPath(PathUnit.Forbidden), component: ForbiddenComponent },
    ],
  },
  {
    path: '**',
    redirectTo: getPath(PathUnit.Dashboard),
  },
];
